import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaBullseye,
  FaTrophy,
  FaChartLine,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import '../../styles/views.css';

const TargetsView = ({ userInfo, API_BASE_URL, auth }) => {
  const [targets, setTargets] = useState([]);
  const [currentPerformance, setCurrentPerformance] = useState(null);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTargetsData();
  }, [userInfo]);

  const fetchTargetsData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userInfo?.officer_id) {
        setError('Officer ID not found. Please contact your administrator.');
        return;
      }

      // Fetch officer performance data including targets
      const response = await axios.get(
        `${API_BASE_URL}/officer-performance/officer/${userInfo.officer_id}`,
        auth
      );

      const data = response.data;
      setTargets(data.targets || []);
      setCurrentPerformance(data.current_performance);
      setPerformanceHistory(data.performance_history || []);

    } catch (err) {
      console.error('Error fetching targets data:', err);
      setError('Failed to load targets data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTargetStatusColor = (target) => {
    if (target.status === 'completed') return '#27ae60';
    if (target.status === 'active') return '#3498db';
    if (target.status === 'inactive') return '#95a5a6';
    return '#f39c12';
  };

  const getTargetStatusIcon = (target) => {
    if (target.status === 'completed') return <FaCheckCircle />;
    if (target.status === 'active') return <FaBullseye />;
    return <FaInfoCircle />;
  };

  const getPerformanceRatingColor = (rating) => {
    switch (rating) {
      case 'excellent': return '#27ae60';
      case 'good': return '#2ecc71';
      case 'average': return '#f39c12';
      case 'poor': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getPerformanceRatingIcon = (rating) => {
    switch (rating) {
      case 'excellent': return <FaTrophy />;
      case 'good': return <FaArrowUp />;
      case 'average': return <FaInfoCircle />;
      case 'poor': return <FaArrowDown />;
      default: return <FaExclamationTriangle />;
    }
  };

  const formatCurrency = (amount) => {
    return `GHS ${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCurrentMonthTarget = () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    return targets.find(target => 
      target.target_period === currentMonth && 
      target.target_type === 'monthly' && 
      target.status === 'active'
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div>Loading targets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '8px',
        padding: '1rem',
        margin: '1rem',
        color: '#c53030'
      }}>
        <FaExclamationTriangle style={{ marginRight: '0.5rem' }} />
        {error}
      </div>
    );
  }

  const currentTarget = getCurrentMonthTarget();

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          margin: 0, 
          fontSize: '2rem', 
          fontWeight: '600', 
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaBullseye style={{ color: '#3498db' }} />
          My Targets & Performance
        </h1>
        <p style={{ 
          margin: '0.5rem 0 0 0', 
          fontSize: '1rem', 
          color: '#7f8c8d' 
        }}>
          Track your revenue collection targets and performance metrics
        </p>
      </div>

      {/* Current Month Target Card */}
      {currentTarget && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
          border: '1px solid #e1e8ed'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1rem'
          }}>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#2c3e50',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <FaCalendarAlt style={{ color: '#3498db' }} />
                Current Month Target
              </h2>
              <p style={{ 
                margin: '0.25rem 0 0 0', 
                color: '#7f8c8d',
                fontSize: '0.9rem'
              }}>
                {currentTarget.target_description || 'Monthly revenue collection target'}
              </p>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: getTargetStatusColor(currentTarget),
              color: 'white',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: '600'
            }}>
              {getTargetStatusIcon(currentTarget)}
              {currentTarget.status?.toUpperCase()}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                TARGET AMOUNT
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3498db' }}>
                {formatCurrency(currentTarget.target_amount)}
              </div>
            </div>

            {currentPerformance && (
              <>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    COLLECTED
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#27ae60' }}>
                    {formatCurrency(currentPerformance.total_collected)}
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    ACHIEVEMENT
                  </div>
                  <div style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold', 
                    color: currentPerformance.achievement_percentage >= 100 ? '#27ae60' : '#f39c12'
                  }}>
                    {parseFloat(currentPerformance.achievement_percentage || 0).toFixed(1)}%
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                    PERFORMANCE
                  </div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: 'bold', 
                    color: getPerformanceRatingColor(currentPerformance.performance_rating),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    {getPerformanceRatingIcon(currentPerformance.performance_rating)}
                    {currentPerformance.performance_rating?.toUpperCase() || 'PENDING'}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Progress Bar */}
          {currentPerformance && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#2c3e50' }}>
                  Progress to Target
                </span>
                <span style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                  {formatCurrency(currentPerformance.total_collected)} / {formatCurrency(currentTarget.target_amount)}
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                backgroundColor: '#e9ecef',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${Math.min(currentPerformance.achievement_percentage || 0, 100)}%`,
                  height: '100%',
                  backgroundColor: currentPerformance.achievement_percentage >= 100 ? '#27ae60' : '#3498db',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Current Target Message */}
      {!currentTarget && (
        <div style={{
          backgroundColor: '#fff8e1',
          border: '1px solid #ffcc02',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <FaInfoCircle style={{ fontSize: '2rem', color: '#ff8f00', marginBottom: '1rem' }} />
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#e65100' }}>
            No Active Target for This Month
          </h3>
          <p style={{ margin: 0, color: '#ef6c00' }}>
            Contact your supervisor to set up your monthly revenue collection target.
          </p>
        </div>
      )}

      {/* All Targets List */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        border: '1px solid #e1e8ed'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FaChartLine style={{ color: '#3498db' }} />
          All Targets
        </h2>

        {targets.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#7f8c8d'
          }}>
            <FaBullseye style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
            <p>No targets have been set for you yet.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{
                    padding: '0.75rem',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Period
                  </th>
                  <th style={{
                    padding: '0.75rem',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Type
                  </th>
                  <th style={{
                    padding: '0.75rem',
                    textAlign: 'right',
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Target Amount
                  </th>
                  <th style={{
                    padding: '0.75rem',
                    textAlign: 'center',
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Status
                  </th>
                  <th style={{
                    padding: '0.75rem',
                    textAlign: 'left',
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    color: '#495057'
                  }}>
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target, index) => (
                  <tr key={target.target_id || index} style={{
                    borderBottom: '1px solid #dee2e6',
                    transition: 'background-color 0.2s ease'
                  }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                        {target.target_period}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                        Created: {formatDate(target.created_at)}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {target.target_type}
                      </span>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'right',
                      fontWeight: '600',
                      color: '#2c3e50'
                    }}>
                      {formatCurrency(target.target_amount)}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.75rem',
                        backgroundColor: getTargetStatusColor(target),
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {getTargetStatusIcon(target)}
                        {target.status?.toUpperCase() || 'PENDING'}
                      </div>
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      color: '#495057',
                      maxWidth: '200px'
                    }}>
                      {target.target_description || 'No description provided'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Performance History */}
      {performanceHistory.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          border: '1px solid #e1e8ed'
        }}>
          <h2 style={{
            margin: '0 0 1.5rem 0',
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <FaTrophy style={{ color: '#f39c12' }} />
            Performance History
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            {performanceHistory.slice(0, 6).map((record, index) => (
              <div key={record.record_id || index} style={{
                padding: '1rem',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#2c3e50'
                  }}>
                    {record.period_value}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: getPerformanceRatingColor(record.performance_rating)
                  }}>
                    {getPerformanceRatingIcon(record.performance_rating)}
                    {record.performance_rating?.toUpperCase() || 'N/A'}
                  </div>
                </div>

                <div style={{ marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                    Collected: <span style={{ fontWeight: '600', color: '#27ae60' }}>
                      {formatCurrency(record.total_collected)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                    Target: <span style={{ fontWeight: '600', color: '#3498db' }}>
                      {formatCurrency(record.target_amount)}
                    </span>
                  </div>
                </div>

                <div style={{
                  width: '100%',
                  height: '6px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    width: `${Math.min(record.achievement_percentage || 0, 100)}%`,
                    height: '100%',
                    backgroundColor: record.achievement_percentage >= 100 ? '#27ae60' : '#3498db',
                    transition: 'width 0.3s ease'
                  }} />
                </div>

                <div style={{
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: record.achievement_percentage >= 100 ? '#27ae60' : '#f39c12',
                  textAlign: 'center'
                }}>
                  {parseFloat(record.achievement_percentage || 0).toFixed(1)}% Achievement
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetsView;
